if (!customElements.get('testing-cartridge-panel')) {
  class TestingCartridgePanel extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      window.addEventListener('euroguard:testing-cartridge-change', this.handleChange, {
        signal: this.abortController.signal,
      });

      this.configs = new Map(
        Array.from(this.querySelectorAll('[data-cartridge-config]')).map((item) => [
          item.dataset.id,
          this.readConfig(item),
        ])
      );

      this.labels = {
        stages: this.dataset.stagesLabel,
        filtration: this.dataset.filtrationLabel,
        capacity: this.dataset.capacityLabel,
        fits: this.dataset.fitsLabel,
        laboratories: this.dataset.laboratoriesLabel,
        reports: this.dataset.reportsLabel,
        headline: this.dataset.headlineTitle,
        emptyHeadline: this.dataset.emptyHeadline,
      };

      this.render(window.EuroguardTestingStore?.cartridge);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
    }

    handleChange = (event) => {
      this.render(event.detail?.cartridge);
    };

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      })[character]);
    }

    lines(value) {
      return String(value || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    }

    readConfig(item) {
      const read = (name) => item.getAttribute(`data-${name}`) || '';
      const standards = [];
      const headline = [];

      for (let index = 1; index <= 5; index += 1) {
        const title = read(`standard-${index}-title`);
        if (title) {
          standards.push({
            title,
            status: read(`standard-${index}-status`) || 'lab',
            label: read(`standard-${index}-label`),
          });
        }
      }

      for (let index = 1; index <= 4; index += 1) {
        const label = read(`result-${index}-label`);
        const value = read(`result-${index}-value`);
        if (label && value) headline.push([label, value]);
      }

      return {
        id: read('id'), image: read('image'), imageAlt: read('image-alt'), title: read('title'),
        product: read('product'), technology: read('technology'), stages: this.lines(read('stages')),
        filtration: read('filtration'), capacity: read('capacity'), lifespan: read('lifespan'),
        fits: read('fits'), laboratories: read('laboratories'), reportText: read('report-text'),
        reportUrl: read('report-url'), standards, resultsIntro: read('results-intro'), headline,
      };
    }

    statusTag(status, title, label) {
      const safeStatus = ['cert', 'lab', 'pending', 'nc'].includes(status) ? status : 'lab';
      return `<span class="eg-testing__tag eg-testing__tag--${safeStatus}">${this.escape(title)}${label ? ` · ${this.escape(label)}` : ''}</span>`;
    }

    specRow(label, content) {
      if (!content) return '';
      return `<div class="testing-cartridge-panel__spec-row"><dt>${this.escape(label)}</dt><dd>${content}</dd></div>`;
    }

    render(cartridgeId) {
      const store = window.EuroguardTestingStore;
      if (!store) return;

      const fallback = store.getCartridge(cartridgeId);
      const config = this.configs.get(fallback.id);
      const data = config || {
        title: fallback.name, image: '', imageAlt: '', product: '', technology: '',
        stages: [], standards: [], headline: [], resultsIntro: '',
        filtration: '', capacity: '', lifespan: '', fits: '', laboratories: '',
        reportText: '', reportUrl: ''
      };

      const image = this.querySelector('[data-image]');
      const media = this.querySelector('[data-media]');
      if (image instanceof HTMLImageElement) {
        if (data.image) image.src = data.image;
        else image.removeAttribute('src');
        image.alt = data.imageAlt || data.product || data.title;
        image.hidden = !data.image;
        media.hidden = !data.image;
      }

      this.querySelector('[data-title]').textContent = data.title;
      this.querySelector('[data-tech]').textContent = [data.product, data.technology].filter(Boolean).join(' · ');
      this.querySelector('[data-standards]').innerHTML = data.standards
        .map((standard) => this.statusTag(standard.status, standard.title, standard.label))
        .join('');

      const stages = data.stages.map((stage) => `<li>${this.escape(stage)}</li>`).join('');
      const capacity = [data.capacity, data.lifespan].filter(Boolean).map((value) => this.escape(value)).join(' · ');
      const report = data.reportText
        ? (data.reportUrl
          ? `<a class="testing-cartridge-panel__report-link" href="${this.escape(data.reportUrl)}">${this.escape(data.reportText)}</a>`
          : this.escape(data.reportText))
        : '';

      this.querySelector('[data-spec]').innerHTML = [
        this.specRow(this.labels.stages, stages ? `<ul class="testing-cartridge-panel__stages">${stages}</ul>` : ''),
        this.specRow(this.labels.filtration, this.escape(data.filtration)),
        this.specRow(this.labels.capacity, capacity),
        this.specRow(this.labels.fits, this.escape(data.fits)),
        this.specRow(this.labels.laboratories, this.escape(data.laboratories)),
        this.specRow(this.labels.reports, report),
      ].join('');

      const headline = this.querySelector('[data-headline]');
      if (data.headline.length) {
        const stats = data.headline.map(([label, value]) => `
          <div class="testing-cartridge-panel__stat">
            <b>${this.escape(value)}</b>
            <span>${this.escape(label)}</span>
          </div>
        `).join('');

        headline.innerHTML = `
          <h3>${this.escape(this.labels.headline)}</h3>
          ${data.resultsIntro ? `<p>${this.escape(data.resultsIntro)}</p>` : ''}
          <div class="testing-cartridge-panel__stats">${stats}</div>
        `;
      } else {
        headline.innerHTML = `<p class="testing-cartridge-panel__empty">${this.escape(this.labels.emptyHeadline)}</p>`;
      }

      this.removeAttribute('aria-busy');
    }
  }

  customElements.define('testing-cartridge-panel', TestingCartridgePanel);
}
